import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, Pagination } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFindUpcomingEvents } from "../../api/queries/useFindUpcomingEvents";
import { useGetBookedEvents } from "../../api/queries/useGetBookedEvents";
import { useAppContext } from "../../Context/AppContext";
import { BookedEvents, Event } from "../../types/Types";
import {
  getEventCardPhase,
  isEventDateStillActive,
  isEventPastForAttendedList,
  parseEventDateAsLocalDay,
  type EventCardPhase,
} from "../../utils/eventDateUtils";
import { isEventEarlyBirdConfigured } from "../../utils/eventEarlyBirdUtils";
import { formatCurrencyAmount } from "../../utils/formatCurrency";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";
import { ensureExplorerProfileSyncedForEventBooking } from "../../explorer/explorerProfileSync";
const PAGE_SIZE = 20;
const EVENT_QUESTIONS_PATH = "/event-questions";
const EVENT_ATTENDEES_PATH = "/event-booking/attendees";

const SPINNER_STYLE: React.CSSProperties = {
  width: "3rem",
  height: "3rem",
};

const LOADING_CONTAINER_STYLE: React.CSSProperties = {
  minHeight: "200px",
};

const CARD_ACTIONS_STYLE: React.CSSProperties = {
  minWidth: "245px",
};

const EVENT_CARD_PHASE_CLASS: Record<EventCardPhase, string> = {
  upcoming: "event-card--upcoming",
  today: "event-card--today",
  expired: "event-card--expired",
};

type EventsListMode = "upcoming" | "attended";

interface EventCardProps {
  event: Event;
  listMode: EventsListMode;
  isBooked: boolean;
  onBook: (eventId: number) => void;
  onViewBooked: (event: BookedEvents) => void;
  onViewAttendees: (eventId: number) => void;
  getBookedEvent: (eventId: number) => BookedEvents | undefined;
}

const EventCard = memo(function EventCard({
  event,
  listMode,
  isBooked,
  onBook,
  onViewBooked,
  onViewAttendees,
  getBookedEvent,
}: EventCardProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage || i18n.language;
  const eventDate = parseEventDateAsLocalDay(event.eventDate);
  const phase: EventCardPhase =
    listMode === "attended" ? "expired" : getEventCardPhase(event.eventDate);
  const hasEarlyBird = isEventEarlyBirdConfigured(event);
  const isEarlyBirdActive =
    hasEarlyBird &&
    event.earlyBirdDateEnd &&
    new Date(event.earlyBirdDateEnd) > new Date();

  const handleViewBookedClick = useCallback(() => {
    const bookedEvent = getBookedEvent(event.id);
    if (bookedEvent) onViewBooked(bookedEvent);
  }, [event.id, getBookedEvent, onViewBooked]);

  const handleViewAttendeesClick = useCallback(() => {
    onViewAttendees(event.id);
  }, [event.id, onViewAttendees]);

  const dynamicFields = useMemo(
    () => ({
      name: event.name ?? "",
      description: event.description ?? "",
      location: event.location ?? "",
    }),
    [event.name, event.description, event.location],
  );
  const { data: translatedFields } = useTranslateDynamicFields(
    dynamicFields,
    dateLocale,
  );
  const tx = (key: keyof typeof dynamicFields, fallback: string) =>
    translatedFields?.[key]?.trim() || fallback;

  const phaseLabel =
    phase === "today"
      ? t("events.today")
      : phase === "upcoming"
        ? t("events.upcoming")
        : t("events.ended");

  return (
    <div
      className={`card rounded-4 overflow-hidden border-0 ${EVENT_CARD_PHASE_CLASS[phase]}`}
    >
      <div className="card-body d-flex flex-column justify-content-between">
        <div className="d-flex justify-content-between flex-md-row flex-column gap-3">
          <div>
            <h5 className="card-title fw-bold mb-2">{tx("name", event.name)}</h5>
            <p className="card-text text-muted">
              {tx("description", event.description ?? "")}
            </p>
            <p className="small text-secondary">
              <strong>{t("events.location")}:</strong>{" "}
              {tx("location", event.location ?? "") || t("common.na")}
            </p>
            <p className="small text-secondary">
              <strong>{t("events.eventDate")}:</strong>{" "}
              {eventDate.toLocaleDateString(dateLocale, {
                day: "numeric",
                month: "long",
                year: "numeric",
              }) || t("common.na")}
            </p>
          </div>
          <div
            className="px-3 event-card-price-col text-md-end"
            style={CARD_ACTIONS_STYLE}
          >
            <span
              className={`badge rounded-pill event-card-phase-badge event-card-phase-badge--${phase} mb-2`}
            >
              {phaseLabel}
            </span>
            <ul className="list-unstyled mb-3 small">
              <li className="event-card-price-line">
                <strong>{t("events.price")}:</strong>{" "}
                {formatCurrencyAmount(event.price, event.currency ?? "gbp")}
              </li>
              {hasEarlyBird && (
                <>
                  <li>
                    <strong>{t("events.earlyBirdDiscount")}:</strong>{" "}
                    {formatCurrencyAmount(
                      event.earlyBirdPrice,
                      event.currency ?? "gbp",
                    )}
                  </li>
                  <li>
                    {isEarlyBirdActive ? (
                      <strong>
                        {t("events.earlyBirdEndsOn")}:{" "}
                        {new Date(event.earlyBirdDateEnd!).toLocaleDateString(
                          dateLocale,
                        )}
                      </strong>
                    ) : (
                      <span className="text-danger">
                        {t("events.earlyBirdEnded")}
                      </span>
                    )}
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
        {listMode === "attended" ? (
          <div className="event-card-actions d-flex gap-2 mt-2 flex-wrap">
            <Button
              className="btn flex-grow-1 btn-success text-white"
              onClick={handleViewBookedClick}
              type="button"
            >
              {t("events.viewBookedEvent")}
            </Button>
            <Button
              className="btn flex-grow-1 btn-primary text-white"
              onClick={handleViewAttendeesClick}
              type="button"
            >
              {t("events.viewAttendees")}
            </Button>
          </div>
        ) : isBooked ? (
          <div className="event-card-actions d-flex gap-2 mt-2 flex-wrap">
            <Button
              className="btn flex-grow-1 btn-success text-white"
              onClick={handleViewBookedClick}
              type="button"
            >
              {t("events.viewBookedEvent")}
            </Button>
            <Button
              className="btn flex-grow-1 btn-primary text-white"
              onClick={handleViewAttendeesClick}
              type="button"
            >
              {t("events.viewAttendees")}
            </Button>
          </div>
        ) : event.seats > 0 ? (
          <div className="event-card-actions mt-2">
            <Button
              className="btn w-100 btn-primary text-white"
              onClick={() => onBook(event.id)}
              type="button"
            >
              {t("events.bookNow")}
            </Button>
          </div>
        ) : (
          <div className="event-card-actions mt-2">
            <Button className="btn w-100 btn-danger text-white" disabled type="button">
              {t("events.soldOut")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
});
EventCard.displayName = "EventCard";

function Events() {
  const { t, i18n } = useTranslation();
  const langKey = i18n.resolvedLanguage || i18n.language;
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<BookedEvents | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<EventsListMode>("upcoming");

  const { objectId } = useAppContext();

  useEffect(() => {
    void ensureExplorerProfileSyncedForEventBooking();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const {
    data: events,
    isPending,
    error,
  } = useFindUpcomingEvents({
    pageSize: PAGE_SIZE,
    page: currentPage,
    searchTerm: debouncedSearchTerm.trim() || undefined,
    enabled: activeTab === "upcoming",
  });
  const {
    data: bookedEvents,
    isPending: bookedEventsPending,
    error: bookedEventsError,
  } = useGetBookedEvents({
    id: objectId || "",
    enabled: !!objectId,
  });

  const handleBook = useCallback(
    async (eventId: number) => {
      try {
        await ensureExplorerProfileSyncedForEventBooking();
      } catch {
        /* best-effort; legacy users without names can still book */
      }
      navigate(`${EVENT_QUESTIONS_PATH}/${eventId}`);
    },
    [navigate],
  );

  const handleViewBooked = useCallback((event: BookedEvents) => {
    setSelectedEvent(event);
    setShowModal(true);
  }, []);

  const handleViewAttendees = useCallback(
    (eventId: number) => {
      navigate(`${EVENT_ATTENDEES_PATH}/${eventId}`);
    },
    [navigate]
  );

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setSelectedEvent(null);
  }, []);

  const handleCancelModalClose = useCallback(() => {
    setShowCancelModal(false);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleOpenCancelModal = useCallback(() => {
    setShowModal(false);
    setShowCancelModal(true);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, citySearch, activeTab]);

  const apiEventsActive = useMemo(() => {
    return (events?.data ?? []).filter((event) =>
      isEventDateStillActive(event.eventDate),
    );
  }, [events?.data]);

  const matchesSearchFilters = useCallback(
    (event: Event) => {
      const q = debouncedSearchTerm.trim().toLowerCase();
      const city = citySearch.trim().toLowerCase();
      const haystack =
        `${event.name ?? ""} ${event.description ?? ""} ${event.location ?? ""}`.toLowerCase();
      const matchesSearch = q === "" || haystack.includes(q);
      const matchesCity =
        city === "" || (event.location ?? "").toLowerCase().includes(city);
      return matchesSearch && matchesCity;
    },
    [debouncedSearchTerm, citySearch],
  );

  // Convert BookedEvents to Event shape for display (API may return array or single object)
  const bookedAsEvents = useMemo((): Event[] => {
    const list: BookedEvents[] = Array.isArray(bookedEvents)
      ? bookedEvents
      : bookedEvents
        ? [bookedEvents as BookedEvents]
        : [];
    const uniqueBookedById = new Map<number, BookedEvents>();
    for (const bookedEvent of list) {
      if (!uniqueBookedById.has(bookedEvent.id)) {
        uniqueBookedById.set(bookedEvent.id, bookedEvent);
      }
    }

    return Array.from(uniqueBookedById.values()).map((b) => ({
        id: b.id,
        name: b.name,
        description: b.description ?? "",
        price: b.price,
        earlyBirdPrice: b.earlyBirdPrice,
        earlyBirdDateEnd: b.earlyBirdDateEnd ?? "",
        seats: b.seats ?? 0,
        location: b.location ?? "",
        productId: "",
        currency: b.currency ?? undefined,
        eventDate: b.eventDate,
      }));
  }, [bookedEvents]);

  // Calculate total pages for pagination
  const totalPages = useMemo(
    () => Math.ceil((events?.totalRecords ?? 0) / PAGE_SIZE),
    [events?.totalRecords],
  );

  const isEventBooked = useCallback(
    (eventId: number) => {
      return (
        Array.isArray(bookedEvents) &&
        bookedEvents.some((bookedEvent) => bookedEvent.id === eventId)
      );
    },
    [bookedEvents],
  );

  const getBookedEvent = useCallback(
    (eventId: number) => {
      return Array.isArray(bookedEvents)
        ? bookedEvents.find((bEvent: BookedEvents) => bEvent.id === eventId)
        : undefined;
    },
    [bookedEvents],
  );

  const handleSearchTermChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    [],
  );

  const handleCitySearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCitySearch(e.target.value);
    },
    [],
  );

  const upcomingEventList = useMemo(() => {
    let list = [...apiEventsActive];
    if (searchTerm.trim() || citySearch.trim()) {
      list = list.filter(matchesSearchFilters);
    }
    return list.sort(
      (a, b) =>
        new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime(),
    );
  }, [apiEventsActive, debouncedSearchTerm, citySearch, matchesSearchFilters]);

  const attendedEventList = useMemo(() => {
    let list = bookedAsEvents.filter((event) =>
      isEventPastForAttendedList(event.eventDate),
    );
    if (searchTerm.trim() || citySearch.trim()) {
      list = list.filter(matchesSearchFilters);
    }
    return list.sort(
      (a, b) =>
        new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime(),
    );
  }, [bookedAsEvents, debouncedSearchTerm, citySearch, matchesSearchFilters]);

  const eventList =
    activeTab === "upcoming" ? upcomingEventList : attendedEventList;

  const showPagination =
    activeTab === "upcoming" && (events?.totalRecords ?? 0) >= PAGE_SIZE;

  const isListLoading =
    activeTab === "upcoming" ? isPending : bookedEventsPending;
  const listError = activeTab === "upcoming" ? error : bookedEventsError;

  const selectedDynamicFields = useMemo(
    () =>
      selectedEvent
        ? {
            name: selectedEvent.name ?? "",
            description: selectedEvent.description ?? "",
            location: selectedEvent.location ?? "",
          }
        : {},
    [selectedEvent],
  );
  const { data: translatedSelected } = useTranslateDynamicFields(
    selectedDynamicFields,
    langKey,
  );
  const txSelected = (
    key: keyof typeof selectedDynamicFields,
    fallback: string,
  ) => translatedSelected?.[key]?.trim() || fallback;

  const handlePageFirst = useCallback(
    () => handlePageChange(1),
    [handlePageChange],
  );
  const handlePagePrev = useCallback(
    () => handlePageChange(currentPage - 1),
    [currentPage, handlePageChange],
  );
  const handlePageNext = useCallback(
    () => handlePageChange(currentPage + 1),
    [currentPage, handlePageChange],
  );
  const handlePageLast = useCallback(
    () => handlePageChange(totalPages),
    [totalPages, handlePageChange],
  );

  return (
    <main
      key={langKey}
      className="page-content w-100 p-4 pt-5 pt-md-5 m-0"
      aria-label={t("events.listingAria")}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .event-card--upcoming {
            box-shadow: 0 0.5rem 1.25rem rgba(0, 0, 0, 0.1);
            background: #fff;
          }
          .event-card--today {
            box-shadow: 0 0.5rem 1.25rem rgba(35, 102, 205, 0.12);
            background: #fff;
            border: 2px solid #3a97a4 !important;
          }
          .event-card--expired {
            box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.06);
            background: #f3f4f6;
          }
          .event-card--expired .card-body {
            background: #f3f4f6;
            color: #5c6570;
          }
          .event-card-price-col {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
          }
          @media (min-width: 768px) {
            .event-card-price-col {
              align-items: flex-end;
            }
          }
          .event-card-phase-badge {
            font-size: 0.68rem;
            font-weight: 700;
            letter-spacing: 0.05em;
            text-transform: uppercase;
          }
          .event-card-phase-badge--today {
            background: #2366cd;
            color: #fff;
          }
          .event-card-phase-badge--upcoming {
            background: #198754;
            color: #fff;
          }
          .event-card-phase-badge--expired {
            background: #fff;
            color: #dc3545;
            border: 1px solid #dc3545;
          }
          .event-card-price-line {
            color: #1a3f8b;
            font-weight: 700;
            font-size: 0.95rem;
          }
          .event-card-actions .btn-primary,
          .event-card-actions .btn-success,
          .event-card-actions .btn-danger {
            color: #ffffff !important;
          }
          .event-card-actions .btn-primary:hover,
          .event-card-actions .btn-primary:focus,
          .event-card-actions .btn-success:hover,
          .event-card-actions .btn-success:focus,
          .event-card-actions .btn-danger:hover,
          .event-card-actions .btn-danger:focus {
            color: #ffffff !important;
          }
        `,
        }}
      />
      <ul className="nav nav-tabs mb-4" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            type="button"
            className={`nav-link ${activeTab === "upcoming" ? "active" : ""}`}
            role="tab"
            aria-selected={activeTab === "upcoming"}
            onClick={() => setActiveTab("upcoming")}
          >
            {t("events.tabUpcoming")}
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            type="button"
            className={`nav-link ${activeTab === "attended" ? "active" : ""}`}
            role="tab"
            aria-selected={activeTab === "attended"}
            onClick={() => setActiveTab("attended")}
          >
            {t("events.tabAttendedEvents")}
          </button>
        </li>
      </ul>

      {/* Search Inputs */}
      <div className="row mb-4 g-3">
        <div className="col-md-6">
          <input
            type="text"
            className="form-control"
            placeholder={t("events.searchEvents")}
            value={searchTerm}
            onChange={handleSearchTermChange}
            aria-label={t("events.searchEvents")}
          />
        </div>

        <div className="col-md-6">
          <input
            type="text"
            className="form-control"
            placeholder={t("events.searchByCity")}
            value={citySearch}
            onChange={handleCitySearchChange}
            aria-label={t("events.searchByCity")}
          />
        </div>
      </div>

      {/* Event List */}
      <div className="d-flex flex-column gap-4">
        {isListLoading ? (
          <div
            className="w-100 h-100 text-center"
            style={LOADING_CONTAINER_STYLE}
          >
            <div
              className="spinner-border text-primary text-center"
              role="status"
              style={SPINNER_STYLE}
            >
              <span className="visually-hidden">{t("messages.loading")}</span>
            </div>
          </div>
        ) : listError ? (
          <p className="text-danger">{t("events.loadFailed")}</p>
        ) : eventList.length > 0 ? (
          eventList.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              listMode={activeTab}
              isBooked={
                activeTab === "attended" ? true : isEventBooked(event.id)
              }
              onBook={handleBook}
              onViewBooked={handleViewBooked}
              onViewAttendees={handleViewAttendees}
              getBookedEvent={getBookedEvent}
            />
          ))
        ) : (
          <p className="text-muted">
            {activeTab === "attended"
              ? t("events.noAttendedEvents")
              : t("events.noMatch")}
          </p>
        )}
      </div>

      {/* Pagination Controls */}
      {events?.totalRecords && showPagination && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First
              onClick={handlePageFirst}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={handlePagePrev}
              disabled={currentPage === 1}
            />
            {[...Array(totalPages)].map((_, i) => (
              <Pagination.Item
                key={i + 1}
                active={i + 1 === currentPage}
                onClick={() => handlePageChange(i + 1)}
              >
                {i + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={handlePageNext}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={handlePageLast}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}

      {/* Booked Event Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("events.bookedDetails")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEvent && (
            <>
              <h5>{txSelected("name", selectedEvent.name)}</h5>
              <p>{txSelected("description", selectedEvent.description ?? "")}</p>
              <ul className="list-unstyled small">
                <li>
                  <strong>{t("events.price")}:</strong>{" "}
                  {formatCurrencyAmount(
                    selectedEvent.price,
                    selectedEvent.currency ?? "gbp",
                  )}
                </li>
                <li>
                  <strong>{t("events.sectorsOfInterest")}:</strong>{" "}
                  {selectedEvent.sectorsOfInterest}
                </li>
                <li>
                  <strong>{t("events.stageOfBusiness")}:</strong>{" "}
                  {selectedEvent.stageOfBusiness}
                </li>
                <li>
                  <strong>{t("events.eventObjectives")}:</strong>{" "}
                  {selectedEvent.eventObjectives}
                </li>
                <li>
                  <strong>{t("events.location")}:</strong>{" "}
                  {txSelected("location", selectedEvent.location ?? "") ||
                    t("common.na")}
                </li>
              </ul>

              <div className="d-flex justify-content-center mt-3">
                <Button
                  variant="danger"
                  onClick={handleOpenCancelModal}
                  type="button"
                >
                  {t("events.cancelBooking")}
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            {t("common.close")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Membership Info Modal */}
      <Modal show={showCancelModal} onHide={handleCancelModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("events.cancelBooking")}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {t("events.cancelBookingHelp")}{" "}
          <strong>enquiries@smbf.global</strong>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCancelModalClose}
            type="button"
          >
            {t("common.close")}
          </Button>
        </Modal.Footer>
      </Modal>
    </main>
  );
}

Events.displayName = "Events";

export default memo(Events);